import { ResizeState } from '../types/resize';

/**
 * State transition utilities for resize operations
 */

export type ResizeStateTransition = 
  | 'START_RESIZE'
  | 'UPDATE_RESIZE'
  | 'END_RESIZE'
  | 'CANCEL_RESIZE';

export interface ResizeAction {
  type: ResizeStateTransition;
  payload?: {
    itemId?: string;
    edge?: 'top' | 'bottom';
    mouseY?: number;
    startTime?: string;
    endTime?: string;
  };
}

/**
 * Reducer function for managing resize state transitions
 */
export const resizeStateReducer = (
  state: ResizeState,
  action: ResizeAction
): ResizeState => {
  switch (action.type) {
    case 'START_RESIZE':
      if (!action.payload?.itemId || !action.payload?.edge || action.payload?.mouseY === undefined) {
        console.warn('START_RESIZE action missing required payload');
        return state;
      }
      
      return {
        ...state,
        isResizing: true,
        itemId: action.payload.itemId,
        resizeEdge: action.payload.edge,
        startMouseY: action.payload.mouseY,
        originalStartTime: action.payload.startTime || state.originalStartTime,
        originalEndTime: action.payload.endTime || state.originalEndTime,
        currentStartTime: action.payload.startTime || state.currentStartTime,
        currentEndTime: action.payload.endTime || state.currentEndTime
      };

    case 'UPDATE_RESIZE':
      if (!state.isResizing) {
        console.warn('UPDATE_RESIZE called when not resizing');
        return state;
      }
      
      return {
        ...state,
        currentStartTime: action.payload?.startTime || state.currentStartTime,
        currentEndTime: action.payload?.endTime || state.currentEndTime
      };

    case 'END_RESIZE':
      if (!state.isResizing) {
        console.warn('END_RESIZE called when not resizing');
        return state;
      }
      
      return {
        isResizing: false,
        itemId: null,
        resizeEdge: null,
        originalStartTime: '',
        originalEndTime: '',
        currentStartTime: '',
        currentEndTime: '',
        startMouseY: 0
      };

    case 'CANCEL_RESIZE':
      return {
        isResizing: false,
        itemId: null,
        resizeEdge: null,
        originalStartTime: '',
        originalEndTime: '',
        currentStartTime: '',
        currentEndTime: '',
        startMouseY: 0
      };

    default:
      console.warn(`Unknown resize action type: ${(action as any).type}`);
      return state;
  }
};

/**
 * Validate resize state transitions
 */
export const validateResizeTransition = (
  currentState: ResizeState,
  action: ResizeAction
): { isValid: boolean; reason?: string } => {
  switch (action.type) {
    case 'START_RESIZE':
      if (currentState.isResizing) {
        return {
          isValid: false,
          reason: 'Cannot start resize when already resizing'
        };
      }
      
      if (!action.payload?.itemId || !action.payload?.edge || action.payload?.mouseY === undefined) {
        return {
          isValid: false,
          reason: 'START_RESIZE requires itemId, edge, and mouseY'
        };
      }
      
      return { isValid: true };

    case 'UPDATE_RESIZE':
      if (!currentState.isResizing) {
        return {
          isValid: false,
          reason: 'Cannot update resize when not resizing'
        };
      }
      
      return { isValid: true };

    case 'END_RESIZE':
    case 'CANCEL_RESIZE':
      if (!currentState.isResizing) {
        return {
          isValid: false,
          reason: 'Cannot end/cancel resize when not resizing'
        };
      }
      
      return { isValid: true };

    default:
      return {
        isValid: false,
        reason: `Unknown action type: ${(action as any).type}`
      };
  }
};

/**
 * Helper function to create resize actions
 */
export const createResizeAction = {
  startResize: (itemId: string, edge: 'top' | 'bottom', mouseY: number, startTime: string, endTime: string): ResizeAction => ({
    type: 'START_RESIZE',
    payload: { itemId, edge, mouseY, startTime, endTime }
  }),

  updateResize: (startTime: string, endTime: string): ResizeAction => ({
    type: 'UPDATE_RESIZE',
    payload: { startTime, endTime }
  }),

  endResize: (): ResizeAction => ({
    type: 'END_RESIZE'
  }),

  cancelResize: (): ResizeAction => ({
    type: 'CANCEL_RESIZE'
  })
};